import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Button, Callout, FormCheckbox, FormInput, FormSelect, Modal, SegmentedControl } from '@/components/UI';
import { useAppQuery } from '@/hooks/useAppQuery';
import { STANDARD_DURATIONS } from '@einhaus/project314-shared';
import type { BarDuration } from '@einhaus/project314-types';
import { slugifyVariantKey, type CreateIdeaLabVariantInput, type IdeaLabVariantDetail, type IdeaLabVariantSummary } from '../ideaLabUtils';

type VariantSourceMode = 'publishedVersion' | 'draft' | 'variant' | 'template';

const SOURCE_OPTIONS: { value: VariantSourceMode; label: string }[] = [
    { value: 'template', label: 'Swing template' },
    { value: 'publishedVersion', label: 'Published entry rules' },
    { value: 'draft', label: 'Saved draft' },
    { value: 'variant', label: 'Copy a variant' }
];

const VARIANT_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

interface CreateVariantModalProps {
    studyId: number;
    variants: IdeaLabVariantSummary[];
    isOpen: boolean;
    onClose: () => void;
    onCreated: (variant: IdeaLabVariantDetail) => void;
}

/**
 * Adds a variant to a study from one of three sources. Editing the rules themselves happens in
 * the Trade Logic editor on drafts or published rules; the Idea Lab snapshot is taken here and
 * stays detached from the library.
 */
const CreateVariantModal = ({ studyId, variants, isOpen, onClose, onCreated }: CreateVariantModalProps) => {
    const queryClient = useQueryClient();
    const [sourceMode, setSourceMode] = useState<VariantSourceMode>('publishedVersion');
    const [definitionId, setDefinitionId] = useState<number | null>(null);
    const [versionId, setVersionId] = useState<number | null>(null);
    const [draftId, setDraftId] = useState<number | null>(null);
    const [sourceVariantId, setSourceVariantId] = useState<number | null>(null);
    const [label, setLabel] = useState('');
    const [variantKey, setVariantKey] = useState('');
    const [keyEdited, setKeyEdited] = useState(false);
    const [description, setDescription] = useState('');
    const [primaryDuration, setPrimaryDuration] = useState<BarDuration>('5m');
    const [parentVariantId, setParentVariantId] = useState<number | null>(null);
    const [isStudyControl, setIsStudyControl] = useState(false);
    const [template, setTemplate] = useState<'breakout' | 'reclaim'>('breakout');
    const [baseDuration, setBaseDuration] = useState<'1d' | '1w'>('1d');
    const [baseBars, setBaseBars] = useState(20);
    const templateQuery = useAppQuery(
        {
            ...trpc.tradeLogicAuthoring.getSwingTemplate.queryOptions({
                template,
                duration: primaryDuration === '5m' ? '5m' : '15m',
                baseDuration,
                baseBars
            }),
            enabled:
                isOpen &&
                sourceMode === 'template' &&
                (primaryDuration === '5m' || primaryDuration === '15m') &&
                baseBars >= 2 &&
                baseBars <= 200,
            staleTime: 60_000
        },
        { fallbackErrorMessage: 'Failed to load swing template' }
    );
    useEffect(() => {
        if (sourceMode === 'template' && primaryDuration !== '5m' && primaryDuration !== '15m') setPrimaryDuration('15m');
    }, [sourceMode, primaryDuration]);

    const importableQuery = useAppQuery(
        {
            ...trpc.ideaLab.listImportableVersions.queryOptions(),
            enabled: isOpen && sourceMode === 'publishedVersion',
            staleTime: 60_000
        },
        { fallbackErrorMessage: 'Failed to load entry rules' }
    );
    const importableDefinitions = useMemo(() => importableQuery.data ?? [], [importableQuery.data]);
    const selectedDefinition = useMemo(
        () => importableDefinitions.find((definition) => definition.definitionId === definitionId) ?? null,
        [importableDefinitions, definitionId]
    );
    const draftsQuery = useAppQuery(
        {
            ...trpc.tradeLogicAuthoring.listDrafts.queryOptions({ kind: 'entry' }),
            enabled: isOpen && sourceMode === 'draft',
            staleTime: 60_000
        },
        { fallbackErrorMessage: 'Failed to load entry drafts' }
    );

    const liveVariants = useMemo(() => variants.filter((variant) => variant.lifecycle !== 'archived'), [variants]);

    useEffect(() => {
        if (!keyEdited) setVariantKey(slugifyVariantKey(label));
    }, [label, keyEdited]);

    // Default to the newest importable version; recovered legacy versions store no payload.
    useEffect(() => {
        if (sourceMode !== 'publishedVersion' || versionId !== null || !selectedDefinition) return;

        const firstImportable = selectedDefinition.versions.find((version) => version.importable);

        if (firstImportable) setVersionId(firstImportable.versionId);
    }, [sourceMode, selectedDefinition, versionId]);

    useEffect(() => {
        if (sourceMode === 'variant' && sourceVariantId !== null) {
            const source = liveVariants.find((variant) => variant.id === sourceVariantId);

            if (source) setPrimaryDuration(source.primaryDuration);
        }
    }, [sourceMode, sourceVariantId, liveVariants]);

    const createMutation = useMutation({
        ...trpc.ideaLab.createVariant.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (variant) => {
            showToast(`Variant "${variant.label}" added`, 'success');
            await queryClient.invalidateQueries(trpc.ideaLab.getStudy.pathFilter());
            onCreated(variant);
        }
    });

    const source: CreateIdeaLabVariantInput['source'] | null = useMemo(() => {
        switch (sourceMode) {
            case 'template':
                return templateQuery.data && !templateQuery.isFetching ? { kind: 'authoringPayload', payload: templateQuery.data } : null;
            case 'publishedVersion':
                return versionId === null ? null : { kind: 'publishedVersion', versionId };
            case 'draft':
                return draftId === null ? null : { kind: 'draft', draftId };
            case 'variant':
                return sourceVariantId === null ? null : { kind: 'variant', variantId: sourceVariantId };
        }
    }, [sourceMode, versionId, draftId, sourceVariantId, templateQuery.data, templateQuery.isFetching]);

    const keyError =
        variantKey !== '' && !VARIANT_KEY_PATTERN.test(variantKey) ? 'Letters, digits, ".", "_" or "-" only (max 64)' : undefined;
    const duplicateKey = variants.some((variant) => variant.variantKey === variantKey);
    const canSubmit =
        source !== null && label.trim() !== '' && variantKey !== '' && !keyError && !duplicateKey && !createMutation.isPending;

    const submit = () => {
        if (!source) return;

        createMutation.mutate({
            studyId,
            variantKey,
            label: label.trim(),
            description: description.trim() === '' ? null : description.trim(),
            primaryDuration,
            ...(sourceMode !== 'variant' && parentVariantId !== null ? { parentVariantId } : {}),
            isStudyControl,
            source
        });
    };

    return (
        <Modal
            footer={
                <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!canSubmit} isLoading={createMutation.isPending} variant="primary" onClick={submit}>
                        Add variant
                    </Button>
                </div>
            }
            isOpen={isOpen}
            size="lg"
            title="Add a variant"
            onClose={onClose}
        >
            <div className="space-y-4">
                <div>
                    <p className="mb-2 text-sm text-zinc-400">Where do the entry rules come from?</p>
                    <SegmentedControl ariaLabel="Variant source" options={SOURCE_OPTIONS} value={sourceMode} onChange={setSourceMode} />
                </div>

                {sourceMode === 'template' && (
                    <div className="space-y-3">
                        <div className="grid gap-3 sm:grid-cols-3">
                            <FormSelect
                                label="Setup"
                                noMargin
                                value={template}
                                options={[
                                    { value: 'breakout', label: 'Base breakout' },
                                    { value: 'reclaim', label: 'Undercut and reclaim' }
                                ]}
                                onChange={(event) => setTemplate(event.target.value === 'breakout' ? 'breakout' : 'reclaim')}
                            />
                            <FormSelect
                                label="Base bars"
                                noMargin
                                value={baseDuration}
                                options={[
                                    { value: '1d', label: 'Daily' },
                                    { value: '1w', label: 'Weekly' }
                                ]}
                                onChange={(event) => {
                                    setBaseDuration(event.target.value === '1w' ? '1w' : '1d');
                                    setBaseBars(event.target.value === '1w' ? 6 : 20);
                                }}
                            />
                            <FormInput
                                label="Completed bars"
                                noMargin
                                type="number"
                                min={2}
                                max={200}
                                value={baseBars}
                                onChange={(event) => setBaseBars(Number(event.target.value))}
                            />
                        </div>
                        <p className="text-sm text-zinc-400">
                            Editable stock research rules with completed-bar confirmation, SPY/QQQ trend filters and frozen base boundaries.
                            Use a regular-session stock universe. Defaults use a rolling range as a base approximation. Review or vary the
                            thresholds before running a study. Idea Lab measures entry outcomes; staged exits need a strategy backtest.
                        </p>
                    </div>
                )}

                {sourceMode === 'publishedVersion' && (
                    <div className="grid gap-3 sm:grid-cols-2">
                        <FormSelect
                            label="Entry rules"
                            noMargin
                            options={[
                                { value: '', label: importableQuery.isLoading ? 'Loading…' : 'Select entry rules' },
                                ...importableDefinitions.map((definition) => {
                                    const importableCount = definition.versions.filter((version) => version.importable).length;

                                    return {
                                        value: definition.definitionId,
                                        label: `${definition.name} · #${definition.definitionId}${definition.isActive ? '' : ' (inactive)'}${
                                            definition.ownerStrategyId !== null ? ` (strategy ${definition.ownerStrategyId})` : ''
                                        }${importableCount === 0 ? ' — no importable version' : ''}`,
                                        disabled: importableCount === 0
                                    };
                                })
                            ]}
                            value={definitionId ?? ''}
                            onChange={(event) => {
                                setDefinitionId(event.target.value === '' ? null : Number(event.target.value));
                                setVersionId(null);
                            }}
                        />
                        <FormSelect
                            disabled={definitionId === null}
                            label="Version"
                            noMargin
                            helpText="Recovered legacy versions store no authoring payload and cannot be imported."
                            options={[
                                { value: '', label: 'Select a version' },
                                ...(selectedDefinition?.versions ?? []).map((version) => ({
                                    value: version.versionId,
                                    label: `v${version.version} (#${version.versionId})${version.importable ? '' : ' — not importable'}`,
                                    disabled: !version.importable
                                }))
                            ]}
                            value={versionId ?? ''}
                            onChange={(event) => setVersionId(event.target.value === '' ? null : Number(event.target.value))}
                        />
                    </div>
                )}

                {sourceMode === 'draft' && (
                    <FormSelect
                        helpText="Drafts saved in the Trade Logic editor. The variant snapshots the draft; later draft edits do not change it."
                        label="Entry draft"
                        noMargin
                        options={[
                            { value: '', label: draftsQuery.isLoading ? 'Loading…' : 'Select a draft' },
                            ...(draftsQuery.data?.drafts ?? []).map((draft) => ({
                                value: draft.id,
                                label: `${draft.label} (#${draft.id})`
                            }))
                        ]}
                        value={draftId ?? ''}
                        onChange={(event) => setDraftId(event.target.value === '' ? null : Number(event.target.value))}
                    />
                )}

                {sourceMode === 'variant' && (
                    <FormSelect
                        helpText="The copy becomes a child of the selected variant, so the report can attribute shared, added, and removed entries."
                        label="Variant to copy"
                        noMargin
                        options={[
                            { value: '', label: 'Select a variant' },
                            ...liveVariants.map((variant) => ({ value: variant.id, label: `${variant.label} (${variant.variantKey})` }))
                        ]}
                        value={sourceVariantId ?? ''}
                        onChange={(event) => setSourceVariantId(event.target.value === '' ? null : Number(event.target.value))}
                    />
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                    <FormInput label="Label" maxLength={255} noMargin value={label} onChange={(event) => setLabel(event.target.value)} />
                    <FormInput
                        error={keyError ?? (duplicateKey ? 'Another variant in this study uses this key' : undefined)}
                        helpText="Stable key inside the study; part of the frozen document id."
                        label="Key"
                        maxLength={64}
                        noMargin
                        value={variantKey}
                        onChange={(event) => {
                            setKeyEdited(true);
                            setVariantKey(event.target.value);
                        }}
                    />
                </div>
                <FormInput
                    label="Description"
                    maxLength={4000}
                    noMargin
                    placeholder="What is different about this variant?"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                    <FormSelect
                        disabled={sourceMode === 'variant'}
                        helpText="Bar duration the rules are evaluated on. Every variant in a run must share it."
                        label="Duration"
                        noMargin
                        options={(sourceMode === 'template'
                            ? STANDARD_DURATIONS.filter((duration) => duration === '5m' || duration === '15m')
                            : STANDARD_DURATIONS
                        ).map((duration) => ({ value: duration, label: duration }))}
                        value={primaryDuration}
                        onChange={(event) => setPrimaryDuration(event.target.value as BarDuration)}
                    />
                    {sourceMode !== 'variant' && (
                        <FormSelect
                            helpText="Optional. Lets the report show what this variant adds or removes versus the parent."
                            label="Parent variant"
                            noMargin
                            options={[
                                { value: '', label: 'None' },
                                ...liveVariants.map((variant) => ({ value: variant.id, label: `${variant.label} (${variant.variantKey})` }))
                            ]}
                            value={parentVariantId ?? ''}
                            onChange={(event) => setParentVariantId(event.target.value === '' ? null : Number(event.target.value))}
                        />
                    )}
                </div>
                <FormCheckbox
                    checked={isStudyControl}
                    label="Use as the study control (the baseline the other variants are compared against)"
                    onChange={(event) => setIsStudyControl(event.target.checked)}
                />
                <Callout variant="info">
                    The variant starts as a draft you can rename and re-source. Freezing compiles it for evaluation; frozen rules never
                    change.
                </Callout>
            </div>
        </Modal>
    );
};

export default CreateVariantModal;
